(() => {
  const drawer = document.querySelector('[data-minireve-cart-drawer]');
  if (!drawer || window.MinireveCart) return;
  const root = drawer.dataset.rootUrl || window.Shopify?.routes?.root || '/';
  let busy = false;
  let failed = false;
  let trigger = null;
  let closingTimer;
  let refreshPending;
  let pendingFocus;
  let focusRevision = 0;
  const originalDisabled = new WeakMap();
  const url = (path) => `${root.replace(/\/$/, '')}/${path}`;
  const text = (name) => drawer.dataset[name] || '';
  const error = (message = '') => {
    const node = drawer.querySelector('[data-cart-drawer-error]');
    if (node) { node.textContent = message; node.hidden = !message; }
  };
  const controls = () => {
    drawer.setAttribute('aria-busy', String(busy));
    drawer.querySelectorAll('[data-cart-quantity-minus], [data-cart-quantity-plus], [data-cart-quantity-input], [data-cart-remove]').forEach((node) => {
      if (!originalDisabled.has(node)) originalDisabled.set(node, Boolean(node.disabled));
      node.disabled = busy || originalDisabled.get(node);
    });
    drawer.querySelectorAll('[name="checkout"]').forEach((node) => { node.disabled = busy || failed || drawer.dataset.empty === 'true'; });
    drawer.querySelectorAll('minireve-care').forEach((node) => { node.inert = busy; });
    const status = drawer.querySelector('[data-cart-drawer-status]');
    if (status) { status.hidden = !busy; status.textContent = busy ? text('loading') : ''; }
  };
  function rememberFocus(node) {
    if (!node || !drawer.contains(node)) return;
    const attribute = ['data-care-checkbox', 'data-cart-quantity-input', 'data-cart-quantity-plus', 'data-cart-quantity-minus', 'data-cart-remove', 'data-cart-drawer-close'].find((name) => node.hasAttribute(name));
    if (!attribute) return;
    pendingFocus = { key: node.closest('[data-key]')?.dataset.key, selector: `[${attribute}]` };
  }
  function restoreFocusWhenReady() {
    if (!pendingFocus || !drawer.open) return;
    const revision = ++focusRevision;
    Promise.resolve(window.MinireveCare?.whenIdle()).catch(() => {}).then(() => {
      if (revision !== focusRevision || busy || !drawer.open || !pendingFocus) return;
      const scope = pendingFocus.key ? Array.from(drawer.querySelectorAll('[data-key]')).find((node) => node.dataset.key === pendingFocus.key) : drawer;
      const candidate = scope?.querySelector(pendingFocus.selector);
      const target = candidate && !candidate.disabled && !candidate.closest('[inert]') ? candidate : drawer.querySelector('[data-cart-drawer-close]');
      pendingFocus = null;
      target?.focus({ preventScroll: true });
    });
  }
  async function request(path, body) {
    const response = await fetch(url(path), {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(20000)
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400 || result.errors) throw new Error(result.description || text('error'));
    return result;
  }
  async function refresh() {
    if (refreshPending) return refreshPending;
    refreshPending = (async () => {
      const endpoint = new URL(root, location.origin);
      endpoint.searchParams.set('section_id', 'minireve-cart-drawer');
      const response = await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error(text('error'));
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = parsed.querySelector('[data-cart-drawer-content]');
      const current = drawer.querySelector('[data-cart-drawer-content]');
      if (!fresh || !current) throw new Error(text('error'));
      const scroll = current.querySelector('[data-cart-drawer-scroll]')?.scrollTop || 0;
      rememberFocus(document.activeElement);
      current.replaceWith(fresh);
      const scroller = fresh.querySelector('[data-cart-drawer-scroll]');
      if (scroller) scroller.scrollTop = scroll;
      const count = fresh.dataset.cartItemCount || '0';
      const clothes = fresh.dataset.cartClothingCount || count;
      drawer.dataset.empty = count === '0' ? 'true' : 'false';
      document.querySelectorAll('[data-minireve-cart-count]').forEach((node) => { node.textContent = clothes; node.hidden = clothes === '0'; });
      controls();
      restoreFocusWhenReady();
    })().finally(() => { refreshPending = null; });
    return refreshPending;
  }
  function show(source) {
    window.clearTimeout(closingTimer);
    if (!drawer.open) {
      trigger = source || document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add('mr-cart-is-open');
    }
    requestAnimationFrame(() => { if (drawer.open) drawer.classList.add('is-open'); });
  }
  function close() {
    pendingFocus = null;
    focusRevision += 1;
    drawer.classList.remove('is-open');
    const finish = () => {
      drawer.close();
      document.documentElement.classList.remove('mr-cart-is-open');
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else closingTimer = window.setTimeout(finish, 320);
  }
  async function sync() {
    if (window.MinireveCare) {
      await window.MinireveCare.whenIdle();
      const result = await window.MinireveCare.sync();
      if (result.changed) return;
    }
    await refresh();
  }
  async function open(source) {
    show(source);
    if (busy) return;
    busy = true; controls(); error();
    try { await sync(); failed = false; }
    catch { failed = true; error(text('error')); }
    finally { busy = false; controls(); restoreFocusWhenReady(); }
  }
  async function mutate(action, source = document.activeElement) {
    if (busy) return;
    rememberFocus(source);
    busy = true; controls(); error();
    try {
      await window.MinireveCare?.whenIdle();
      await action();
      await sync();
      failed = false;
    } catch (err) {
      failed = true;
      try { await refresh(); } catch {}
      error(err.message || text('error'));
    } finally { busy = false; controls(); restoreFocusWhenReady(); }
  }
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const source = event.target.closest('a,button');
    if (!source) return;
    if (source.matches('[data-cart-drawer-close]')) { event.preventDefault(); close(); return; }
    const link = source.closest('a[href]');
    const target = link ? new URL(link.href, location.origin) : null;
    if (source.matches('[data-minireve-cart-open]') || (target?.origin === location.origin && target.pathname.replace(/\/$/, '') === url('cart').replace(/\/$/, '') && !link.hasAttribute('data-care-refresh') && !link.hasAttribute('data-cart-full-page'))) {
      event.preventDefault(); open(source); return;
    }
    if (!drawer.contains(source)) return;
    const row = source.closest('[data-cart-line]');
    if (!row) return;
    const input = row.querySelector('[data-cart-quantity-input]');
    let quantity;
    if (source.matches('[data-cart-remove]')) quantity = 0;
    else if (source.matches('[data-cart-quantity-plus]')) quantity = Number(input.value) + 1;
    else if (source.matches('[data-cart-quantity-minus]')) quantity = Math.max(0, Number(input.value) - 1);
    else return;
    event.preventDefault();
    mutate(() => request('cart/change.js', { id: row.dataset.key, quantity }), source);
  });
  drawer.addEventListener('change', (event) => {
    if (!event.target.matches('[data-cart-quantity-input]')) return;
    const row = event.target.closest('[data-cart-line]');
    const quantity = Number(event.target.value);
    if (!Number.isInteger(quantity) || quantity < 0) { event.target.reportValidity(); return; }
    mutate(() => request('cart/change.js', { id: row.dataset.key, quantity }), event.target);
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (drawer.contains(form)) {
      if (busy || failed) { event.preventDefault(); event.stopImmediatePropagation(); }
      return;
    }
    if (!form.matches('.mr-product-form')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy || !form.reportValidity()) return;
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Number(data.get('quantity') || 1);
    if (!id || !Number.isInteger(quantity) || quantity < 1) return;
    show(event.submitter);
    mutate(() => request('cart/add.js', { items: [{ id, quantity }] }));
  }, true);
  drawer.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  drawer.addEventListener('click', (event) => { if (event.target === drawer) { const rect = drawer.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right) close(); } });
  window.MinireveCart = { refresh, open, close };
})();
